import type { TaxReturnDeliveryStatus } from '@st/sdk'
import type { Section } from '@st/util/section'
import { SortState, SortTransitions } from '@st/util/sort'

// Types related to the filters
export type QuestionnareFilterOptionKey =
  | 'not_seen_yet'
  | 'not_seen_yet_not_sent'
  | 'not_seen_yet_sent'
  | 'in_progress'
  | 'submitted'

export type FolderListStatusFilter =
  | { kind: 'organizer'; status: QuestionnareFilterOptionKey }
  | { kind: 'delivery'; status: TaxReturnDeliveryStatus }

type StatusFilterOption = {
  label: string
  filter: FolderListStatusFilter
}

const ORGANIZER_STATUS_SECTION: Section<string, StatusFilterOption> = {
  heading: 'Organizer status',
  items: [
    { label: 'Not seen yet', filter: { kind: 'organizer', status: 'not_seen_yet' } },
    {
      label: 'Not seen yet, Not sent',
      filter: { kind: 'organizer', status: 'not_seen_yet_not_sent' }
    },
    { label: 'Not seen yet, Sent', filter: { kind: 'organizer', status: 'not_seen_yet_sent' } },
    { label: 'In progress', filter: { kind: 'organizer', status: 'in_progress' } },
    { label: 'Submitted', filter: { kind: 'organizer', status: 'submitted' } }
  ]
}

const DELIVERY_STATUS_SECTION: Section<string, StatusFilterOption> = {
  heading: 'Delivery status',
  items: [
    { label: 'Not delivered', filter: { kind: 'delivery', status: 'not_delivered' } },
    { label: 'Delivered', filter: { kind: 'delivery', status: 'delivered' } },
    { label: 'In progress', filter: { kind: 'delivery', status: 'in_progress' } },
    { label: 'Signed', filter: { kind: 'delivery', status: 'signed' } }
  ]
}

export const STATUS_FILTER_SECTIONS = [ORGANIZER_STATUS_SECTION, DELIVERY_STATUS_SECTION]
const STATUS_FILTER_OPTIONS = STATUS_FILTER_SECTIONS.flatMap((section) => section.items)

export function statusFilterId(filter: FolderListStatusFilter): string {
  return `${filter.kind}:${filter.status}`
}

export function statusFilterForId(id: string | undefined): FolderListStatusFilter | undefined {
  if (id == undefined) return undefined
  const option = STATUS_FILTER_OPTIONS.find((option) => statusFilterId(option.filter) == id)
  if (!option) throw new Error(`Unknown status filter: ${id}`)
  return option.filter
}

export const UNASSIGNED = 'UNASSIGNED'

export type STFolderListFilters = {
  keywords: string
  year?: number
  status?: FolderListStatusFilter
  type?: string
  assigneeId?: string
  showArchivedFolders: boolean
}

export const FOLDER_LIST_DEFAULT_FILTERS: STFolderListFilters = {
  keywords: '',
  showArchivedFolders: false,
  assigneeId: undefined
}

export type FolderListSortColumn =
  | 'entities'
  | 'createdAt'
  | 'year'
  | 'type'
  | 'checklist'
  | 'documents'
  | 'clientOrganizer'
  | 'clientOrganizer.updatedAt'
  | 'delivery'

export const FOLDER_DEFAULT_SORT: SortState<FolderListSortColumn> = {
  column: 'createdAt',
  direction: 'desc'
}

export const FOLDER_SORT_TRANSITIONS: SortTransitions<FolderListSortColumn> = {
  'entities': ['asc', 'desc'],
  'createdAt': ['asc', 'desc'],
  'clientOrganizer.updatedAt': ['asc', 'desc'],
  'year': ['desc', 'asc'],
  'clientOrganizer': ['desc', 'asc'],
  'type': ['asc', 'desc'],
  'checklist': ['desc', 'asc'],
  'documents': ['desc', 'asc'],
  'delivery': ['desc', 'asc']
}
